import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as api from '../api/api';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { FiEdit, FiRefreshCw, FiLink } from 'react-icons/fi';
import LoadingSpinner from '../components/LoadingSpinner';

const money = (v) => `$${Number(v || 0).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}`;

const Performance = () => {
  const navigate = useNavigate();
  const [livePositions, setLivePositions] = useState([]);
  const [equityCurve, setEquityCurve] = useState([]);
  const [health, setHealth] = useState(null);
  const [riskCapacity, setRiskCapacity] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const [posRes, equityRes, healthRes, riskRes] = await Promise.allSettled([
        api.getLivePositions(),
        api.getEquityCurve(),
        api.getLocalHealth(),
        api.getRiskCapacity(),
      ]);
      if (posRes.status === 'fulfilled') setLivePositions(posRes.value.data || []);
      else if (posRes.reason?.response?.status === 503) setLivePositions([]);
      if (equityRes.status === 'fulfilled') {
        setEquityCurve((equityRes.value.data || []).map(d => ({...d, timestamp:new Date(d.timestamp).toLocaleDateString()})));
      }
      if (healthRes.status === 'fulfilled') setHealth(healthRes.value.data || null);
      if (riskRes.status === 'fulfilled') setRiskCapacity(riskRes.value.data || null);
    } catch (e) {
      console.error('Failed to fetch live performance', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const timer=setInterval(fetchData,5000);
    return()=>clearInterval(timer);
  }, []);

  const handleModifyStop = async (ticker) => {
    const raw=prompt(`New stop-loss price for ${ticker}`);
    if (!raw || Number.isNaN(Number(raw))) return;
    try {
      await api.modifyStopLoss(ticker,Number(raw));
      await fetchData();
    } catch (e) {
      alert(e.response?.data?.detail || 'Stop modification failed.');
    }
  };

  if (loading) return <div className="workspace-page"><LoadingSpinner/></div>;

  const unrealized=livePositions.reduce((sum,p)=>sum+Number(p.unrealized_pl||0),0);
  const brokerConnected = Boolean(health?.broker_connected);

  return <div className="workspace-page">
    <div className="page-header">
      <div className="page-header-copy">
        <div className="eyebrow">Broker state</div>
        <h1 className="page-title">Live Positions</h1>
        <p className="page-description">Current AlgoStock-managed positions and portfolio equity. Manual IBKR positions remain outside AlgoStock ownership.</p>
      </div>
      <div className="page-actions"><button className="ws-button" onClick={fetchData}><FiRefreshCw/> Refresh</button></div>
    </div>

    <div className="metric-grid" style={{marginBottom:'1rem'}}>
      <div className="metric-card">
        <div className="metric-label">Open positions</div>
        <div className="metric-value">{brokerConnected ? livePositions.length : '—'}</div>
        <div className="metric-sub">{brokerConnected ? 'AlgoStock tracked' : 'Broker offline'}</div>
      </div>
      <div className="metric-card">
        <div className="metric-label">Unrealized P/L</div>
        <div className={`metric-value ${brokerConnected ? (unrealized>=0?'positive':'negative') : ''}`}>{brokerConnected ? money(unrealized) : '—'}</div>
        <div className="metric-sub">{brokerConnected ? 'Current broker snapshot' : 'Unavailable until IBKR connects'}</div>
      </div>
      <div className="metric-card">
        <div className="metric-label">Risk capacity</div>
        <div className="metric-value">{riskCapacity?.available_risk == null ? '—' : money(riskCapacity.available_risk)}</div>
        <div className="metric-sub">{riskCapacity?.configured ? 'Available AlgoStock risk' : 'Configure portfolio risk limit'}</div>
      </div>
      <div className="metric-card">
        <div className="metric-label">Safety</div>
        <div className="metric-value position-safety-value">{health?.live_unlocked ? 'LIVE' : 'PAPER'}</div>
        <div className="metric-sub">{health?.live_unlocked ? 'Review immediately' : 'Paper lock active'}</div>
      </div>
    </div>

    <div className="section-stack">
      <section className="ws-panel pad">
        <div className="section-header"><h2 className="section-title">Positions</h2><span className="section-meta">5s refresh</span></div>
        {livePositions.length ? <div className="ws-table-wrap"><table className="ws-table"><thead><tr><th>Ticker</th><th className="number">Qty</th><th className="number">Entry</th><th className="number">Current</th><th className="number">Stop</th><th className="number">Unrealized</th><th>Action</th></tr></thead><tbody>
          {livePositions.map(p=><tr key={p.ticker}><td><strong className="ticker">{p.ticker}</strong></td><td className="number">{p.qty}</td><td className="number">{money(p.avg_entry_price)}</td><td className="number">{money(p.current_price)}</td><td className="number">{p.initial_stop_price?money(p.initial_stop_price):'—'}</td><td className={`number ${Number(p.unrealized_pl)>=0?'positive':'negative'}`}>{money(p.unrealized_pl)}</td><td><button className="icon-action" onClick={()=>handleModifyStop(p.ticker)} title="Modify stop"><FiEdit/></button></td></tr>)}
        </tbody></table></div> : brokerConnected
          ? <div className="empty-state"><strong>No AlgoStock positions</strong>Nothing is currently open or managed by the execution engine.</div>
          : <div className="position-connection-gate">
              <div className="position-gate-icon"><FiLink/></div>
              <div><strong>IBKR Paper is not connected</strong><span>Position and P/L fields stay unavailable rather than showing a false zero portfolio.</span></div>
              <button className="ws-button" onClick={()=>navigate('/workspace-settings')}><FiLink/> Connect IBKR Paper</button>
            </div>}
      </section>

      <section className="ws-panel pad">
        <div className="section-header"><h2 className="section-title">Equity Curve</h2><span className="section-meta">Hourly local snapshots</span></div>
        {equityCurve.length ? <div style={{width:'100%',height:320,minWidth:0,minHeight:320}}>
          <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={320}>
            <LineChart data={equityCurve} margin={{top:8,right:12,left:8,bottom:8}}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(128,145,170,.13)"/>
              <XAxis dataKey="timestamp" stroke="#718096" tick={{fontSize:11}}/>
              <YAxis stroke="#718096" tick={{fontSize:11}} tickFormatter={v=>`$${Number(v).toLocaleString()}`}/>
              <Tooltip contentStyle={{background:'#111721',border:'1px solid #273246',borderRadius:10}} formatter={v=>money(v)}/>
              <Line type="monotone" dataKey="equity_value" name="Equity" stroke="#4d8dff" strokeWidth={2} dot={false}/>
            </LineChart>
          </ResponsiveContainer>
        </div> : <div className="empty-state"><strong>{brokerConnected ? 'No equity history yet' : 'Equity history unavailable'}</strong>{brokerConnected ? 'Snapshots appear after the runtime records account equity.' : 'Connect IBKR Paper before AlgoStock can record broker equity snapshots.'}</div>}
      </section>
    </div>
  </div>;
};

export default Performance;
